import IntegrationsCard from '@/components/settings/IntegrationsCard';
import SignInCard from '@/components/settings/SignInCard';
import AccessPolicyCard from '@/components/settings/AccessPolicyCard';
import MembersCard from '@/components/settings/MembersCard';
import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useOrganisation } from '@/lib/OrganisationContext';
import { Building2, Users, Save, CheckCircle2, Clock, Fingerprint } from 'lucide-react';

const SECTORS = [
  'Logistics & Transport', 'Healthcare', 'Education', 'Retail', 'Finance & Insurance',
  'Manufacturing', 'Public Sector', 'Technology', 'Construction', 'Hospitality', 'Other',
];

export default function Settings() {
  const { user } = useAuth();
  const { selectedOrg, tenantRevision } = useOrganisation();
  const selectedMembership = user?.organisations?.find((membership) => membership.id === selectedOrg?.id);
  const selectedRole = selectedMembership?.role || (selectedOrg?.id === user?.organisation_id ? user?.org_role : null);
  const selectedIsProvider = selectedMembership?.is_provider ?? selectedOrg?.is_provider ?? false;
  const providerCustomerControl = !!user?.is_provider && !selectedIsProvider;
  const canManageMembers = !!user && (selectedIsProvider ? selectedRole === 'owner' : providerCustomerControl || ['owner', 'admin'].includes(selectedRole || ''));
  const canManageOwnership = !!user && (selectedIsProvider ? selectedRole === 'owner' : providerCustomerControl || selectedRole === 'owner');
  const canManageSignIn = !!user && (user.is_provider || ['owner', 'admin'].includes(selectedRole || ''));
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ name: '', sector: '', size: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('org');
  const [liveMsg, setLiveMsg] = useState('');
  const settingsGeneration = useRef(0);
  const tabRefs = useRef({});
  const tabOrder = canManageMembers ? ['org', 'log', 'members', ...(canManageSignIn ? ['sso'] : [])] : ['org', 'log', ...(canManageSignIn ? ['sso'] : [])];

  function handleTabKeyDown(event, tabId) {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const current = tabOrder.indexOf(tabId);
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = tabOrder[(current + delta + tabOrder.length) % tabOrder.length];
    setActiveTab(next);
    tabRefs.current[next]?.focus();
  }

  async function loadSettings() {
    const requestGeneration = ++settingsGeneration.current;
    const targetId = selectedOrg?.id;
    setLoading(true);
    setLoadError('');
    setOrg(null);
    setForm({ name: '', sector: '', size: '' });
    setLogs([]);
    if (!targetId) {
      setLoading(false);
      if (requestGeneration === settingsGeneration.current) setLoadError('Select an organisation to load its settings.');
      return;
    }
    try {
      const [o, actLogs] = await Promise.all([
        base44.entities.Organisation.get(targetId),
        base44.entities.ActivityLog.list('-created_date', 50),
      ]);
      if (requestGeneration !== settingsGeneration.current) return;
      setOrg(o || null);
      setForm({ name: o?.name || '', sector: o?.sector || '', size: o?.size || '' });
      setLogs(Array.isArray(actLogs) ? actLogs : []);
    } catch (error) {
      if (requestGeneration === settingsGeneration.current) setLoadError(error?.message || 'Settings could not be loaded.');
    } finally {
      if (requestGeneration === settingsGeneration.current) setLoading(false);
    }
  }

  useEffect(() => {
    loadSettings();
    return () => { settingsGeneration.current += 1; };
  }, [selectedOrg?.id, tenantRevision]);
  useEffect(() => {
    if (!canManageMembers && activeTab === 'members') setActiveTab('org');
  }, [canManageMembers, activeTab]);

  async function handleSave() {
    const targetId = selectedOrg?.id;
    const saveGeneration = settingsGeneration.current;
    if (!targetId || !org?.id || org.id !== targetId || loadError) return;
    setSaving(true);
    setSaved(false);
    try {
      const updated = await base44.entities.Organisation.update(targetId, form);
      if (saveGeneration !== settingsGeneration.current || selectedOrg?.id !== targetId) return;
      setOrg(updated);
      setSaved(true);
      setLiveMsg('Organisation profile saved successfully');
      setTimeout(() => { setSaved(false); setLiveMsg(''); }, 3000);
    } catch (error) {
      if (saveGeneration === settingsGeneration.current && selectedOrg?.id === targetId) {
        setLoadError(error?.message || 'Organisation profile could not be saved.');
      }
    } finally {
      if (saveGeneration === settingsGeneration.current && selectedOrg?.id === targetId) setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-[#0D9488] rounded-full animate-spin" aria-hidden="true"></div>
        <span className="sr-only">Loading settings…</span>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div aria-live="polite" aria-atomic="true" className="sr-only">{liveMsg}</div>

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-[#0F1C2E]">Settings</h1>
        <p className="text-slate-500 mt-1">Organisation profile and activity log</p>
      </header>

      {loadError && <div className="mb-5 flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between" role="alert"><span>{loadError}</span><button type="button" onClick={loadSettings} className="min-h-11 rounded-lg border border-destructive/40 px-3 py-2 font-semibold hover:bg-destructive/10">Retry</button></div>}

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Settings sections">
        <button ref={(element) => { tabRefs.current.org = element; }} tabIndex={activeTab === 'org' ? 0 : -1} onKeyDown={(event) => handleTabKeyDown(event, 'org')} role="tab" aria-selected={activeTab === 'org'} aria-controls="org-panel" onClick={() => setActiveTab('org')} className={`flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${activeTab === 'org' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          <Building2 className="w-4 h-4" aria-hidden="true" /> Organisation
        </button>
        <button ref={(element) => { tabRefs.current.log = element; }} tabIndex={activeTab === 'log' ? 0 : -1} onKeyDown={(event) => handleTabKeyDown(event, 'log')} role="tab" aria-selected={activeTab === 'log'} aria-controls="log-panel" onClick={() => setActiveTab('log')} className={`flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${activeTab === 'log' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          <Clock className="w-4 h-4" aria-hidden="true" /> Activity Log
        </button>
        {canManageMembers && (
          <button ref={(element) => { tabRefs.current.members = element; }} tabIndex={activeTab === 'members' ? 0 : -1} onKeyDown={(event) => handleTabKeyDown(event, 'members')} role="tab" aria-selected={activeTab === 'members'} aria-controls="members-panel" onClick={() => setActiveTab('members')} className={`flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${activeTab === 'members' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            <Users className="w-4 h-4" aria-hidden="true" /> Members
          </button>
        )}
        {canManageSignIn && (
          <button ref={(element) => { tabRefs.current.sso = element; }} tabIndex={activeTab === 'sso' ? 0 : -1} onKeyDown={(event) => handleTabKeyDown(event, 'sso')} role="tab" aria-selected={activeTab === 'sso'} aria-controls="sso-panel" onClick={() => setActiveTab('sso')} className={`flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] ${activeTab === 'sso' ? 'bg-[#0F1C2E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            <Fingerprint className="w-4 h-4" aria-hidden="true" /> Sign-in
          </button>
        )}
      </div>

      {/* Organisation panel */}
      {activeTab === 'org' && (
        <div id="org-panel" role="tabpanel" aria-label="Organisation settings">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <h2 className="text-lg font-bold text-[#0F1C2E] mb-6">Organisation Profile</h2>
            <div className="space-y-5 max-w-lg">
              <div>
                <label htmlFor="org-name" className="block text-sm font-medium text-slate-700 mb-1.5">Organisation Name <span className="text-red-500" aria-label="required">*</span></label>
                <input
                  id="org-name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                  aria-required="true"
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent"
                  placeholder="e.g. Rivus Fleet Solutions"
                />
              </div>

              <div>
                <label htmlFor="org-sector" className="block text-sm font-medium text-slate-700 mb-1.5">Sector</label>
                <select
                  id="org-sector"
                  value={form.sector}
                  onChange={e => setForm(p => ({ ...p, sector: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                >
                  <option value="">Select sector…</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="org-size" className="block text-sm font-medium text-slate-700 mb-1.5">Organisation Size</label>
                <select
                  id="org-size"
                  value={form.size}
                  onChange={e => setForm(p => ({ ...p, size: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                >
                  <option value="">Select size…</option>
                  <option value="1-49">1–49 employees</option>
                  <option value="50-249">50–249 employees</option>
                  <option value="250-999">250–999 employees</option>
                  <option value="1000+">1,000+ employees</option>
                </select>
              </div>

              <div className="pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !org?.id || !!loadError || !form.name}
                  aria-live="polite"
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#0D9488] text-white font-semibold rounded-xl hover:bg-teal-700 disabled:opacity-60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488] focus-visible:outline-offset-2"
                >
                  {saved ? <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> : <Save className="w-4 h-4" aria-hidden="true" />}
                  {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Profile'}
                </button>
              </div>
            </div>
          </div>

          <IntegrationsCard orgName={org?.name} />

          {/* WCAG info */}
          <div className="mt-6 bg-teal-50 border border-teal-100 rounded-2xl p-6">
            <h2 className="font-bold text-teal-800 mb-2">Accessibility Compliance</h2>
            <p className="text-sm text-teal-700 leading-relaxed">
              This platform is built to <strong>WCAG 2.2 Level AA</strong> standards. Use the Accessibility button in the toolbar to adjust contrast, text, motion, presentation and optional app audio. Changes apply immediately and are saved to your account.
            </p>
          </div>
        </div>
      )}

      {/* Activity Log panel */}
      {activeTab === 'log' && (
        <div id="log-panel" role="tabpanel" aria-label="Activity log">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-bold text-[#0F1C2E]">Audit Log</h2>
              <p className="text-xs text-slate-400 mt-0.5">{logs.length} events recorded</p>
            </div>
            {logs.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" aria-hidden="true" />
                <p className="text-sm">No activity recorded yet.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-50" role="list" aria-label="Activity log entries">
                {logs.map(log => (
                  <li key={log.id} className="flex items-start gap-4 px-6 py-4">
                    <span className="w-2 h-2 rounded-full bg-[#0D9488] flex-shrink-0 mt-2" aria-hidden="true"></span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#0F1C2E]">{log.action}</p>
                      {log.detail && <p className="text-xs text-slate-500 mt-0.5">{log.detail}</p>}
                      {log.user_name && <p className="text-xs text-slate-400 mt-0.5">By: {log.user_name}</p>}
                    </div>
                    <time className="text-xs text-slate-300 whitespace-nowrap" dateTime={log.created_date}>
                      {log.created_date ? new Date(log.created_date).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'members' && canManageMembers && (
        <div id="members-panel" role="tabpanel" aria-label="Members settings">
          <MembersCard organisationId={selectedOrg?.id} organisationName={org?.name || selectedOrg?.name} canManage={canManageMembers} canManageOwnership={canManageOwnership} readOnly={(selectedOrg?.status || 'active') !== 'active'} />
        </div>
      )}

      {/* Sign-in (SSO) panel */}
      {activeTab === 'sso' && canManageSignIn && (
        <div id="sso-panel" role="tabpanel" aria-label="Single sign-on settings">
          <AccessPolicyCard organisationId={selectedOrg?.id} onReviewMembers={() => { setActiveTab('members'); window.setTimeout(() => document.getElementById(`members-card-${String(selectedOrg?.id || 'organisation').replace(/[^a-zA-Z0-9_-]/g, '-')}`)?.scrollIntoView({ block: 'start' }), 0); }} />
          <SignInCard />
        </div>
      )}
    </div>
  );
}
